export interface TimeScale {
  /** Total lengde i posisjonsenheter. */
  readonly length: number;
  /** Epoch-ms -> posisjon. Klemmes til [0, length]. */
  toPosition(epochMs: number): number;
  /** Posisjon -> epoch-ms. Invers av toPosition. */
  toTime(position: number): number;
}

export interface TimeScaleOptions {
  /** Minste posisjonsavstand mellom to pafolgende tidspunkt. Standard 1. */
  readonly minStep?: number;
  /** Storste posisjonsavstand et enkelt tidsgap kan bidra med. Standard 6. */
  readonly maxStep?: number;
}

const DEFAULT_MIN_STEP = 1;
const DEFAULT_MAX_STEP = 6;

interface ScaleData {
  readonly times: readonly number[];
  readonly positions: readonly number[];
  readonly length: number;
}

export function createTimeScale(
  epochMsSorted: number[],
  options: TimeScaleOptions = {},
): TimeScale {
  const minStep = sanitizeStep(options.minStep, DEFAULT_MIN_STEP);
  const maxStep = sanitizeStep(options.maxStep, DEFAULT_MAX_STEP);
  const low = Math.min(minStep, maxStep);
  const high = Math.max(minStep, maxStep);
  const data = createScaleData(epochMsSorted, low, high);

  return {
    length: data.length,
    toPosition: (epochMs) => toPosition(data, epochMs),
    toTime: (position) => toTime(data, position),
  };
}

function sanitizeStep(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

function createScaleData(
  timesInput: readonly number[],
  minStep: number,
  maxStep: number,
): ScaleData {
  const finiteSorted = timesInput
    .filter(Number.isFinite)
    .slice()
    .sort((a, b) => a - b);
  const times = uniqueSorted(finiteSorted);
  if (times.length === 0) {
    return { times: [], positions: [0], length: 0 };
  }
  if (times.length === 1) {
    return { times, positions: [0], length: 0 };
  }

  const gaps = createPositiveGaps(times);
  const medianGap = median(gaps) || 1;
  const positions = [0];
  let cursor = 0;

  for (let i = 0; i < gaps.length; i += 1) {
    const step = gapToStep(gaps[i], medianGap, minStep, maxStep);
    cursor += step;
    positions.push(cursor);
  }

  return { times, positions, length: cursor };
}

function uniqueSorted(values: readonly number[]): number[] {
  if (values.length === 0) return [];
  const out = [values[0]];
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] !== out[out.length - 1]) {
      out.push(values[i]);
    }
  }
  return out;
}

function createPositiveGaps(times: readonly number[]): number[] {
  const gaps: number[] = [];
  for (let i = 0; i < times.length - 1; i += 1) {
    gaps.push(times[i + 1] - times[i]);
  }
  return gaps;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

function gapToStep(gap: number, medianGap: number, minStep: number, maxStep: number): number {
  if (maxStep <= minStep) return minStep;
  const safeMedian = Math.max(medianGap, 1);
  const ratio = Math.max(gap, 0) / safeMedian;
  // Log-komprimering gir mer plass til klynger uten at ekstremt store hull tar over.
  const normalized = Math.log1p(ratio) / Math.log(2);
  const blend = normalized / (1 + normalized);
  return minStep + (maxStep - minStep) * blend;
}

function toPosition(data: ScaleData, epochMs: number): number {
  if (data.times.length === 0 || !Number.isFinite(epochMs)) return 0;
  if (data.times.length === 1) return 0;
  if (epochMs <= data.times[0]) return 0;
  if (epochMs >= data.times[data.times.length - 1]) return data.length;

  const right = upperBound(data.times, epochMs);
  const left = right - 1;
  const t0 = data.times[left];
  const t1 = data.times[right];
  const p0 = data.positions[left];
  const p1 = data.positions[right];
  const factor = (epochMs - t0) / (t1 - t0);
  return p0 + (p1 - p0) * factor;
}

function toTime(data: ScaleData, position: number): number {
  if (data.times.length === 0 || !Number.isFinite(position)) return 0;
  if (data.times.length === 1) return data.times[0];
  const clamped = clamp(position, 0, data.length);
  if (clamped <= 0) return data.times[0];
  if (clamped >= data.length) return data.times[data.times.length - 1];

  const right = upperBound(data.positions, clamped);
  const left = right - 1;
  const p0 = data.positions[left];
  const p1 = data.positions[right];
  const t0 = data.times[left];
  const t1 = data.times[right];
  const factor = (clamped - p0) / (p1 - p0);
  return t0 + (t1 - t0) * factor;
}

function upperBound(values: readonly number[], target: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const mid = low + ((high - low) >> 1);
    if (values[mid] <= target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
